import React, { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { apiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, LoadingTable, ErrorState, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DataTable } from '../components/DataTable';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ShieldCheck, DatabaseBackup, Download, RotateCcw, Trash2, Plus, Clock, HardDriveDownload } from 'lucide-react';
import { toast } from 'sonner';
import { formatNumber } from '../lib/format';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import UsersPanel from '../components/UsersPanel';
import AuditPanel from '../components/AuditPanel';

function fmtBytes(b) {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}
function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}
const KIND = {
  auto: { label: 'Automatico', cls: 'bg-[hsl(var(--info))]/12 text-[hsl(var(--info))] border-[hsl(var(--info))]/30' },
  manual: { label: 'Manuale', cls: 'bg-accent text-accent-foreground' },
  pre_restore: { label: 'Pre-ripristino', cls: 'bg-[hsl(var(--warning))]/14 text-[hsl(var(--warning-foreground))] border-[hsl(var(--warning))]/30' },
};

export default function Impostazioni() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [note, setNote] = useState('');
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = () => { setError(false); api.backupsList().then(setData).catch(() => setError(true)); };
  useEffect(() => { load(); }, []);

  const doCreate = async () => {
    setCreating(true);
    try { await api.backupCreate(note); toast.success('Backup creato'); setCreateOpen(false); setNote(''); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };
  const doRestore = async () => {
    setBusy(true);
    try { const r = await api.backupRestore(confirmRestore.id); toast.success('Ripristino completato'); setConfirmRestore(null); load(); console.log('restore', r); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const doDelete = async () => {
    setBusy(true);
    try { await api.backupDelete(confirmDelete.id); toast.success('Backup eliminato'); setConfirmDelete(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const doDownload = async (row) => {
    setDownloading(row.id);
    try {
      const full = await api.backupDownload(row.id);
      const blob = new Blob([JSON.stringify(full, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `backup_fier_${row.created_at?.slice(0, 10)}_${row.id.slice(0, 8)}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Download avviato');
    } catch (e) { toast.error(apiError(e)); } finally { setDownloading(null); }
  };

  if (error) return (<div><PageHeader title="Impostazioni" /><ErrorState onRetry={load} /></div>);
  if (!data) return (<div><PageHeader title="Impostazioni" /><LoadingTable /></div>);

  const backups = data.backups || [];
  const sched = data.scheduler || {};

  const columns = [
    { key: 'created_at', header: 'Data e ora', render: (r) => <span className="font-medium">{fmtDate(r.created_at)}</span>, sortValue: (r) => r.created_at },
    { key: 'kind', header: 'Tipo', render: (r) => { const k = KIND[r.kind] || KIND.manual; return <Badge variant="outline" className={k.cls}>{k.label}</Badge>; }, sortValue: (r) => r.kind },
    { key: 'note', header: 'Note', render: (r) => <span className="text-muted-foreground text-xs">{r.note || '—'}</span> },
    { key: 'total_docs', header: 'Record', align: 'right', render: (r) => formatNumber(r.total_docs) },
    { key: 'size_bytes', header: 'Dimensione', align: 'right', render: (r) => fmtBytes(r.size_bytes) },
    { key: 'created_by', header: 'Autore', render: (r) => <span className="text-xs">{r.created_by || 'sistema'}</span> },
    { key: 'actions', header: 'Azioni', sortable: false, align: 'right', render: (r) => (
      <div className="flex items-center justify-end gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Scarica" disabled={downloading === r.id} onClick={() => doDownload(r)} data-testid="backup-download-button"><Download className="h-4 w-4" /></Button>
        {isAdmin && <Button variant="ghost" size="icon" className="h-8 w-8" title="Ripristina" onClick={() => setConfirmRestore(r)} data-testid="backup-restore-button"><RotateCcw className="h-4 w-4" /></Button>}
        {isAdmin && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Elimina" onClick={() => setConfirmDelete(r)} data-testid="backup-delete-button"><Trash2 className="h-4 w-4" /></Button>}
      </div>
    ) },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Impostazioni" description="Sicurezza dei dati, backup, utenti e registro attività" />

      <Tabs defaultValue="backup">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="backup" data-testid="tab-backup">Backup</TabsTrigger>
          {isAdmin && <TabsTrigger value="utenti" data-testid="tab-utenti">Utenti</TabsTrigger>}
          {isAdmin && <TabsTrigger value="audit" data-testid="tab-audit">Registro attività</TabsTrigger>}
        </TabsList>

        <TabsContent value="backup" className="mt-4 space-y-6">
      {/* Info sicurezza dati */}
      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-12 lg:col-span-8 p-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-accent grid place-items-center shrink-0"><ShieldCheck className="h-5 w-5 text-foreground" /></div>
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">I tuoi dati sono al sicuro</h3>
              <p className="text-sm text-muted-foreground">Tutti i dati (piani, ricavi, costi, personale, controlli di gestione e utenti) sono salvati in modo persistente sul database. I backup vengono conservati <b>nel database stesso</b>, quindi restano disponibili anche dopo aggiornamenti dell’applicazione.</p>
              <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-1">
                <li>Backup <b>automatico ogni notte</b> alle 02:00 e a ogni avvio del sistema.</li>
                <li>Backup <b>manuale</b> on-demand con annotazione.</li>
                <li>Ripristino con <b>backup di sicurezza automatico</b> creato prima di ogni ripristino.</li>
                <li>Esportazione di ogni backup in file <b>JSON</b> scaricabile.</li>
              </ul>
            </div>
          </div>
        </Card>
        <Card className="col-span-12 lg:col-span-4 p-5">
          <div className="flex items-center gap-2 mb-3"><Clock className="h-4 w-4 text-foreground" /><h3 className="text-sm font-semibold">Backup automatico</h3></div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Stato</span>{sched.enabled ? <Badge className="bg-[hsl(var(--success))] text-white">Attivo</Badge> : <Badge variant="secondary">Non attivo</Badge>}</div>
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Frequenza</span><span className="font-medium">Ogni notte 02:00</span></div>
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Fuso orario</span><span className="font-medium">{sched.timezone || 'Europe/Rome'}</span></div>
            {sched.next_run && <div className="flex items-center justify-between"><span className="text-muted-foreground">Prossimo</span><span className="font-medium">{fmtDate(sched.next_run)}</span></div>}
          </div>
        </Card>
      </div>

      {/* Registro backup */}
      <div className="flex items-center justify-between gap-3">
        <div><h3 className="text-sm font-semibold flex items-center gap-2"><DatabaseBackup className="h-4 w-4" />Registro dei backup</h3><p className="text-xs text-muted-foreground mt-0.5">{backups.length} backup · collezioni incluse: {(data.collections || []).join(', ')}</p></div>
        {isAdmin && <Button onClick={() => { setNote(''); setCreateOpen(true); }} data-testid="backup-create-button"><Plus className="h-4 w-4 mr-1.5" />Crea backup ora</Button>}
      </div>

      {backups.length === 0 ? (
        <EmptyState title="Nessun backup" description="Crea il primo backup manuale o attendi quello automatico notturno." icon={HardDriveDownload}
          action={isAdmin && <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Crea backup ora</Button>} />
      ) : (
        <DataTable columns={columns} rows={backups} />
      )}

      {!isAdmin && <p className="text-xs text-muted-foreground">Solo la Direzione (admin) può creare, ripristinare o eliminare i backup. Puoi comunque consultarli e scaricarli.</p>}
        </TabsContent>

        {isAdmin && <TabsContent value="utenti" className="mt-4"><UsersPanel /></TabsContent>}
        {isAdmin && <TabsContent value="audit" className="mt-4"><AuditPanel /></TabsContent>}
      </Tabs>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="form-modal">
          <DialogHeader><DialogTitle>Crea un nuovo backup</DialogTitle></DialogHeader>
          <div className="grid gap-1.5 py-2">
            <Label>Nota (opzionale)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. Prima della revisione del piano" data-testid="backup-note-input" />
            <span className="text-[11px] text-muted-foreground">Verrà salvato uno snapshot completo di tutti i dati.</span>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button><Button onClick={doCreate} disabled={creating} data-testid="backup-create-confirm">{creating ? 'Creazione…' : 'Crea backup'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirmRestore} onOpenChange={(o) => !o && setConfirmRestore(null)}
        title="Ripristinare questo backup?"
        description={confirmRestore ? `Tutti i dati attuali verranno sostituiti con lo snapshot del ${fmtDate(confirmRestore.created_at)}. Verrà comunque creato un backup di sicurezza prima del ripristino.` : ''}
        confirmText="Ripristina" onConfirm={doRestore} loading={busy} />
      <ConfirmDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Eliminare questo backup?"
        description={confirmDelete ? `Il backup del ${fmtDate(confirmDelete.created_at)} verrà eliminato definitivamente.` : ''}
        onConfirm={doDelete} loading={busy} />
    </div>
  );
}
